import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useUserContext } from '../Context/UserContext';
import { fetchUserAttributes } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';

const DEFAULT_NEXT = '/session';

function normalizePath(p?: string | null): string | null {
  if (!p) return null;
  try {
    const url = new URL(p, window.location.origin);
    return url.pathname + url.search + url.hash;
  } catch {
    return p.startsWith('/') ? p : `/${p}`;
  }
}

export default function SecureAccessPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { email: ctxEmail, refreshUser } = useUserContext();

  const qp = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const rawNext = qp.get('next') || '';
  const role = (qp.get('role') || '').toLowerCase().trim();
  const id = (qp.get('id') || qp.get('sessionId') || qp.get('sid') || '').trim();

  // ✅ Compute a single target once
  const safeNext = useMemo(() => {
    const fromQuery = normalizePath(rawNext);
    if (fromQuery && fromQuery !== '/') return fromQuery;

    if (id && role) {
      const rid = encodeURIComponent(id);
      if (role.startsWith('coll')) return `/coll/${rid}`;
      if (role.startsWith('init')) return `/session/${rid}`;
    }

    try {
      const pendingSessionId = localStorage.getItem('pendingSessionId') || '';
      const registerSource = (localStorage.getItem('registerSource') || '').toLowerCase();
      if (pendingSessionId && registerSource) {
        const rid = encodeURIComponent(pendingSessionId);
        if (registerSource.startsWith('coll')) return `/coll/${rid}`;
        if (registerSource.startsWith('init')) return `/session/${rid}`;
      }
    } catch {}

    try {
      const persisted = normalizePath(localStorage.getItem('nextAfterAuth'));
      if (persisted && persisted !== '/') return persisted;
    } catch {}

    return DEFAULT_NEXT;
  }, [rawNext, role, id]);

  const here = `${location.pathname}${location.search || ''}${location.hash || ''}`;

  // Persist redirect hints (accept role/id from query; never infer here)
  useEffect(() => {
    try {
      if (safeNext) localStorage.setItem('nextAfterAuth', safeNext);
      if (id) localStorage.setItem('pendingSessionId', id);
      if (role) {
        const normalized =
          role.startsWith('coll') ? 'collaborator' :
          role.startsWith('init') ? 'initiator' : '';
        if (normalized) localStorage.setItem('registerSource', normalized);
      }
    } catch {}
  }, [safeNext, role, id]);

  const lsEmail = useMemo(() => {
    try {
      const v = localStorage.getItem('email') || '';
      return v && v.includes('@') ? v : '';
    } catch {
      return '';
    }
  }, []);

  const [redirecting, setRedirecting] = useState(false);
  const didNavigate = useRef(false);
  const go = (path: string) => {
    if (didNavigate.current) return;
    didNavigate.current = true;
    setRedirecting(true);
    navigate(path, { replace: true });
  };

  // One gentle check — do not spam
  useEffect(() => {
    (async () => {
      try {
        const attrs = await fetchUserAttributes();
        const em = (attrs as any)?.email || (attrs as any)?.preferred_username || '';
        if (em && em.includes('@')) {
          try { localStorage.setItem('email', em); } catch {}
        }
        await refreshUser?.();

        if (here !== safeNext) go(safeNext);
      } catch {
        // not signed in; show buttons
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeNext, here]);

  // Listen once; still debounced
  useEffect(() => {
    const unsub = Hub.listen('auth', async ({ payload: { event } }) => {
      if (event === 'signedIn' || event === 'tokenRefresh') {
        try { await refreshUser?.(); } catch {}
        if (here !== safeNext) go(safeNext);
      }
    });
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeNext, here]);

  const isAuthed = !!(ctxEmail && ctxEmail.includes('@')) || !!lsEmail;
  useEffect(() => {
    if (isAuthed && here !== safeNext) go(safeNext);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed, safeNext, here]);

  if (redirecting) {
    return (
      <div className="auth-container" style={{ maxWidth: 560, margin: '3rem auto' }}>
        <h2>Secure Area</h2>
        <p className="text-muted">You’re signed in. Redirecting…</p>
      </div>
    );
  }

  return (
    <div className="auth-container" style={{ maxWidth: 560, margin: '3rem auto' }}>
      <h2>Secure Area</h2>
      <p className="text-muted">This is a secure app. Register or Login</p>
      <div
        style={{
          display: 'flex',
          gap: '0.75rem',
          marginTop: '1rem',
          justifyContent: 'flex-end',
          flexWrap: 'wrap',
        }}
      >
        <Link
          className="btn"
          style={{ backgroundColor: '#34A853', color: '#FFFFFF', fontWeight: 'bold', padding: '0.85rem 1.25rem', fontSize: '1.05rem' }}
          to={safeNext ? `/login?next=${encodeURIComponent(safeNext)}` : '/login'}
        >
          Log In
        </Link>
        <Link
          className="btn"
          style={{ backgroundColor: '#34A853', color: '#FFFFFF', fontWeight: 'bold', padding: '0.85rem 1.25rem', fontSize: '1.05rem' }}
          to={safeNext ? `/register?next=${encodeURIComponent(safeNext)}` : '/register'}
        >
          Register
        </Link>
        <button
          className="btn"
          style={{ backgroundColor: '#34A853', color: '#FFFFFF', fontWeight: 'bold', padding: '0.85rem 1.25rem', fontSize: '1.05rem' }}
          onClick={() => navigate('/')}
        >
          Home
        </button>
      </div>
    </div>
  );
}
